import type { FieldOption } from './opensearch-field-mapper';
import { getEnumFormatForField } from './enum-label-registry';

/** Tests the declared date type and date/timestamp flags; may return undefined when the flags are absent. */
export function isDateType(field: FieldOption) {
  return field.type === 'date' || field.isDate || field.isTimestamp;
}

/** Recognizes integer, long, float, double, short, and byte field types; other types return false. */
export function isNumericType(field: FieldOption) {
  return ['integer', 'long', 'float', 'double', 'short', 'byte'].includes(field.type);
}

/** Returns true only for the boolean field type. */
export function isBooleanType(field: FieldOption) {
  return field.type === 'boolean';
}

/** Reports whether the static enum list is nonempty; an absent list returns undefined. */
export function hasEnumValues(field: FieldOption) {
  return field.enumValues && field.enumValues.length > 0;
}

/** Returns static enum values as select items, using registered labels when present; absent enum values return an empty list. */
export function getEnumItems(field: FieldOption) {
  if (!field.enumValues) return [];
  const enumFormat = getEnumFormatForField(field.key);
  return field.enumValues.map((v) => ({ key: v, label: enumFormat?.valueMap[v] ?? v }));
}

/** Returns whether the field has a nonempty fetched distinct-value list; absent entries return false. */
export function hasDistinctValues(
  field: FieldOption,
  distinctValuesMap: Record<string, string[]> | undefined
) {
  const vals = distinctValuesMap?.[field.key];
  return vals != null && vals.length > 0;
}

/** Returns fetched distinct values as select items with registered enum labels when present; absent entries return an empty list. */
export function getDistinctItems(
  field: FieldOption,
  distinctValuesMap: Record<string, string[]> | undefined
) {
  const vals = distinctValuesMap?.[field.key];
  if (!vals) return [];
  const enumFormat = getEnumFormatForField(field.key);
  return vals.map((v) => ({ key: v, label: enumFormat?.valueMap[v] ?? v }));
}

/**
 * Select items for an enum-backed filter: prefer the fetched distinct set (the
 * values actually reachable under the current build + sibling filters, already
 * enum-labelled) and fall back to the field's full static enum list only when
 * no set has been fetched.
 *
 * A PRESENT entry — even an empty `[]` — means the reachable set was fetched
 * and is genuinely empty (sibling/build filters left no rows), so the dropdown
 * must show nothing, not every enum value. Only an ABSENT entry (fetch still
 * loading, failed, or over the distinct cap, all of which delete the key)
 * falls back to the full enum. `hasDistinctValues` can't make this distinction
 * (it requires length > 0), so check presence directly.
 */
export function getEnumFilterItems(
  field: FieldOption,
  distinctValuesMap: Record<string, string[]> | undefined
) {
  return distinctValuesMap?.[field.key] !== undefined
    ? getDistinctItems(field, distinctValuesMap)
    : getEnumItems(field);
}

/** Returns true for keyword or text field types. */
export function isTextType(field: FieldOption) {
  return field.type === 'keyword' || field.type === 'text';
}

/** Stringifies the value stored under key, returning an empty string for null or undefined. */
export function getFilterValue(key: string, filterValues: Record<string, unknown>) {
  const val = filterValues[key];
  return val != null ? String(val) : '';
}

/** Get the range start (gte) value from a filter values map */
export function getFilterRangeStart(key: string, filterValues: Record<string, unknown>) {
  const val = filterValues[`${key}__gte`];
  return val != null ? String(val) : '';
}

/** Get the range end (lte) value from a filter values map */
export function getFilterRangeEnd(key: string, filterValues: Record<string, unknown>) {
  const val = filterValues[`${key}__lte`];
  return val != null ? String(val) : '';
}
